/**
 * Types and functions to merge properties into Doc. Generally, these are all pure functions that produce a new Doc with
 * the updates applied.
 * @module
 */
import type { Annotation, LinearDimensionDefinition } from "building-types/annotation/annotationTypes";
import { AnnotationType } from "building-types/annotation/annotationTypesEnum";
import type { CatalogItem } from "building-types/catalogItemTypes";
import {
  ComponentVerticalPlacementType,
  createRemappingVerticalPlacement,
  createReplicationVerticalPlacement,
  type ComponentInstance,
} from "building-types/components/componentInstanceTypes";
import type { DataTable } from "building-types/dataTables/dataTable";
import type { Doc } from "building-types/doc";
import type { DocEntityId } from "building-types/doc/docEntityId";
import { docEntityIdFromString } from "building-types/doc/docEntityId";
import type { DocEntityTypeToValueType } from "building-types/entities/docEntityTypeToValueType";
import type { PlanAreaMeasurementShape } from "building-types/entities/measurementTypes";
import type { FacadeSpanStratum, FacadeSweepStratum } from "building-types/facades/facadeTypes";
import type {
  AnnotationHandle,
  CatalogItemHandle,
  DocEntityHandle,
  EdgeModifierHandle,
  ElectricalRuleHandle,
  FacadeSpanStratumHandle,
  FacadeSweepStratumHandle,
  GlobalParameterHandle,
  LocalParameterHandle,
  OptionHandle,
  PropAttributesHandle,
  RoofSideHandle,
  SettingsHandle,
  ShapeHandle,
  SiteShapeHandle,
  ViewportHandle,
  ViewTemplateHandle,
} from "building-types/handles/docEntityHandleTypes";
import type { LabelableEntityHandle } from "building-types/handles/labelableEntityHandle";
import type { EdgeModifier } from "building-types/modifiers/edgeModifierTypes";
import type { DocOption } from "building-types/options/optionSet";
import type { GlobalParameter, LocalParameter } from "building-types/options/parameter";
import { ParameterType } from "building-types/options/parameter";
import type { PropAttributes } from "building-types/props/propTypes";
import type { ElectricalRule } from "building-types/settings/electricalRuleTypes";
import type { RoofEdgeOverrides } from "building-types/settings/roofTypes";
import type { Settings } from "building-types/settings/settings";
import type { SettingsFragment } from "building-types/settings/settingsFragment";
import type { SiteShape } from "building-types/site/siteShape";
import type { Viewport, ViewTemplate } from "building-types/views/planSets";
import type { Table } from "common/data/table";
import { assertUnreachable } from "common/utils/assertUnreachable";
import objectKeyFilter from "common/utils/objectKeyFilter";
import { batchMergeIn, mergeIn } from "common/utils/objectUtils";
import type { RecursivePartial } from "common/utils/types";
import { docGuardComponentInstanceLevelElimination } from "./docComponentInstanceDeletionGuard";
import { componentChangeCreatesCircularDependency } from "./docComponentMembers";
import { docComponentSourceLevels } from "./docComponentSourceLevels";
import { docGetCatalogItemParentCatalog } from "./docGetCatalogItem";
import { docGetCatalogItemTableFieldName } from "./docGetCatalogItemTableFieldName";
import { docMergeFloorProps } from "./docMergeFloorProps";
import { docSyncPlanViewHostLevel } from "./docPlanViewHostLevel";
import { docGetMeasurementShape } from "./docShapes";
import { docUpdateEntity } from "./docUpdateEntity";
import { docMergeElevationOptionSet } from "./elevation";
import { DocEntityHandleSet } from "./entity/DocEntityHandleSet";
import { docEntityHandleIsLabelable } from "./entity/docEntityHandleTools";
import {
  ComponentCircularDependencyErrorNotification,
  MultipleReportableUnitLabelsErrorNotification,
  ReportableUnitDesignatorLabelErrorNotification,
} from "./entity/docEntityTransactErrorNotification";
import { docEntityTypeToDocTableName } from "./entity/docEntityTypeToDocTableName";
import docGetEntity from "./entity/docGetEntity";
import { isComponentMemberEntityHandle } from "./entity/docGetEntityComponent";
import { docGetTableEntity } from "./entity/docGetTableEntity";
import { parameterFormatName } from "./parameterCoreTools";
import { linearDimensionDefinitionCheckInvariants, noteAutoName } from "./utils/annotation";
import {
  docEntityHasReportableUnitLabelConflict,
  docLabelsBreakOptionSetReportableUnitCap,
  labelNamePredicate,
} from "./utils/labelTools";

export function docMergeSettingsProps(doc: Doc, handle: SettingsHandle, props: Partial<Settings>): Doc {
  doc = mergeIn(doc, ["settings", handle.id], props);
  const settings = doc.settings[handle.id] as any;

  // remove undefined fields from settings
  const filteredSettings = objectKeyFilter(settings, (key) => settings[key] !== undefined) as any as SettingsFragment;

  return {
    ...doc,
    settings: {
      ...doc.settings,
      [handle.id]: filteredSettings,
    },
  };
}

export function docMergeFacadeSpanStrataProps(
  doc: Doc,
  handle: FacadeSpanStratumHandle,
  props: Partial<FacadeSpanStratum>,
): Doc {
  return mergeIn(doc, ["facadeSpanStrata", handle.id], props);
}

export function docMergeFacadeSweepStrataProps(
  doc: Doc,
  handle: FacadeSweepStratumHandle,
  props: Partial<FacadeSweepStratum>,
): Doc {
  return mergeIn(doc, ["facadeSweepStrata", handle.id], props);
}

export function docMergeRoofSideProps(doc: Doc, handle: RoofSideHandle, props: Partial<RoofEdgeOverrides>): Doc {
  const { side, parent } = handle;

  const roof = docGetTableEntity(doc, parent);
  if (!roof) {
    return doc;
  }

  const roofSideOverrides = {
    ...roof.roofSideOverrides,
    [side]: {
      ...roof.roofSideOverrides[side],
      ...props,
    },
  };

  return docMergeEntityProps(doc, parent, { roofSideOverrides });
}

export function docMergeElectricalRuleProps(
  doc: Doc,
  handle: ElectricalRuleHandle,
  props: Partial<ElectricalRule>,
): Doc {
  const electricalRule = docGetEntity(doc, handle) as ElectricalRule;
  return mergeIn(doc, ["settings", handle.settingsHandle.id, "rules", handle.id], { ...electricalRule, ...props });
}

export function docMergeEdgeModifierProps(doc: Doc, handle: EdgeModifierHandle, props: Partial<EdgeModifier>): Doc {
  return mergeIn(doc, ["edgeModifiers", handle.id], props);
}

export function docMergeSiteShapeProps(doc: Doc, handle: SiteShapeHandle, props: Partial<SiteShape>): Doc {
  return mergeIn(doc, ["siteShapes", handle.id], props);
}

export function docMergeMeasurementShapeProps(
  doc: Doc,
  handle: ShapeHandle,
  props: Partial<PlanAreaMeasurementShape>,
): Doc {
  switch (handle.parent.type) {
    case "MEASUREMENT":
      return mergeIn(doc, ["measurements", handle.parent.id, "shapes", handle.shapeId], props);
    default:
      return doc;
  }
}

export function docMergeAnnotationProps(doc: Doc, handle: AnnotationHandle, props: Partial<Annotation>): Doc {
  const { id: annotationId } = handle;
  if (!doc.annotation[annotationId]) {
    return doc;
  }

  const labels = props.labels;
  if (labels) {
    for (const labelId in labels) {
      if (docEntityHasReportableUnitLabelConflict(doc, handle, labelId)) {
        throw new ReportableUnitDesignatorLabelErrorNotification();
      }
    }
  }

  const annot = doc.annotation[annotationId];

  // Auto-name notes if we are not currently renaming them and the text has changed
  if (annot.type === AnnotationType.NOTE && !annot.name && !props["name"] && !!props["text"]) {
    props = {
      ...props,
      name: noteAutoName(annot, props["text"]),
    };
  }

  if (annot.type === AnnotationType.LINEAR_DIMENSION) {
    const annotAfter: LinearDimensionDefinition = {
      ...annot,
      ...(props as Partial<LinearDimensionDefinition>),
    };

    if (!linearDimensionDefinitionCheckInvariants(annotAfter)) {
      return doc;
    }
  }
  return mergeIn(doc, ["annotation", annotationId], props);
}

export function docMergeCatalogItemProps(doc: Doc, handle: CatalogItemHandle, props: Partial<CatalogItem>): Doc {
  const catalogHandle = docGetCatalogItemParentCatalog(doc, handle);
  if (!catalogHandle) {
    return doc;
  }

  const catalog = docGetTableEntity(doc, catalogHandle);
  if (!catalog) {
    return doc;
  }

  const fieldName = docGetCatalogItemTableFieldName(doc, handle);
  if (!fieldName) {
    return doc;
  }

  return mergeIn(doc, ["settings", catalog.id, fieldName, handle.itemId], props);
}

export function docMergeOptionProps(doc: Doc, handle: OptionHandle, props: Partial<DocOption>): Doc {
  return mergeIn(doc, ["optionSets", handle.optionSetId, "options", handle.optionKey], props);
}

export function docMergePropAttributesProps(
  doc: Doc,
  handle: PropAttributesHandle,
  props: Partial<PropAttributes>,
): Doc {
  if (handle.settingsHandle?.id) {
    return mergeIn(doc, ["settings", handle.settingsHandle.id, "propTypes", handle.id], props);
  } else {
    return mergeIn(doc, ["propAttributes", handle.id], props);
  }
}

export function docMergeLabelTableProps(
  doc: Doc,
  handles: LabelableEntityHandle[] | undefined,
  props: Table<boolean>,
): Doc {
  if (!handles || handles.length === 0) {
    return doc;
  }

  const pathValuePairs: [(string | number)[], Table<boolean>][] = [];

  for (const handle of handles) {
    switch (handle.type) {
      case "PROP": {
        if (handle.settingsHandle?.id) {
          pathValuePairs.push([["settings", handle.settingsHandle.id, "propTypes", handle.id, "labels"], props]);
        } else {
          pathValuePairs.push([["propAttributes", handle.id, "labels"], props]);
        }
        break;
      }
      case "PLAN_VIEW": {
        pathValuePairs.push([["planViews", handle.id, "viewFilter", "labels"], props]);
        break;
      }
      case "ELEVATION_VIEW": {
        pathValuePairs.push([["elevationViews", handle.id, "viewFilter", "labels"], props]);
        break;
      }
      case "SECTION_VIEW": {
        pathValuePairs.push([["sectionViews", handle.id, "viewFilter", "labels"], props]);
        break;
      }
      case "COMPONENT_INSTANCE":
      case "CONTENT_TEMPLATE_INSTANCE":
      case "ROOM":
      case "ROOF":
      case "FLOOR":
      case "FLOOR_SYSTEM":
      case "FOUNDATION":
      case "STAIR_SYSTEM":
      case "RAILING":
      case "FREEFORM_WALL":
      case "WALL_PLANE_ASSEMBLY":
      case "ELECTRICAL_DEVICE":
      case "OPTION_SET":
      case "ANNOTATION":
      case "CEILING":
      case "SHEET":
      case "PROP_INSTANCE":
      case "VIEW_TEMPLATE_OVERRIDE": {
        const tableName = docEntityTypeToDocTableName[handle.type];
        if (!tableName) {
          break;
        }
        pathValuePairs.push([[tableName, handle.id, "labels"], props]);
        break;
      }
      case "CONTENT_TEMPLATE": {
        pathValuePairs.push([["contentTemplates", handle.id, "labels"], props]);
        break;
      }
      default:
        assertUnreachable(handle);
    }
  }

  return batchMergeIn(doc, pathValuePairs);
}

export function docMergeViewTemplateOverridesProps(
  doc: Doc,
  handles: (ViewTemplateHandle | ViewportHandle)[] | undefined,
  viewTemplateOverrideId: DocEntityId<"VIEW_TEMPLATE_OVERRIDE">,
): Doc {
  if (!handles || handles.length === 0) {
    return doc;
  }

  // Dedupe first: batchMergeIn reads the pre-batch doc, so duplicate handles would each pass the
  // includes check and enqueue another append.
  const pathValuePairs: [(string | number)[], DocEntityId<"VIEW_TEMPLATE_OVERRIDE">[]][] = [];

  for (const handle of new DocEntityHandleSet(handles)) {
    switch (handle.type) {
      case "VIEW_TEMPLATE": {
        if (doc.viewTemplates[handle.id].viewTemplateOverrideIds.includes(viewTemplateOverrideId)) {
          continue;
        }
        const viewTemplateOverrideIdsField: keyof ViewTemplate = "viewTemplateOverrideIds";

        pathValuePairs.push([["viewTemplates", handle.id, viewTemplateOverrideIdsField], [viewTemplateOverrideId]]);
        break;
      }
      case "VIEWPORT": {
        if (doc.viewports[handle.id].viewTemplateOverrideIds.includes(viewTemplateOverrideId)) {
          continue;
        }
        const viewTemplateOverrideIdsField: keyof Viewport = "viewTemplateOverrideIds";

        pathValuePairs.push([["viewports", handle.id, viewTemplateOverrideIdsField], [viewTemplateOverrideId]]);
        break;
      }
      default:
        assertUnreachable(handle);
    }
  }

  return batchMergeIn(doc, pathValuePairs);
}

export function docMergeParameterProps(
  doc: Doc,
  handle: LocalParameterHandle | GlobalParameterHandle,
  props: Partial<LocalParameter | GlobalParameter>,
): Doc {
  // variable names may not have white space
  if (typeof props.name === "string") {
    props = {
      ...props,
      name: parameterFormatName(props.name),
    };
  }

  // set default subtypes
  if (props.type === ParameterType.VIEW_TEMPLATE && !props.viewType) {
    props = {
      ...props,
      viewType: "PLAN_VIEW",
    };
  } else if (props.type === ParameterType.OPTION && !props.optionSetId) {
    props = {
      ...props,
      optionSetId: docEntityIdFromString("OPTION_SET", "elevationStyle"),
    };
  }

  return docUpdateEntity(doc, handle.type, handle.id, props);
}

function docMergeComponentInstanceProps(
  doc: Doc,
  handle: Extract<DocEntityHandle, { type: "COMPONENT_INSTANCE" }>,
  props: RecursivePartial<ComponentInstance>,
): Doc {
  const instance = docGetTableEntity(doc, handle);
  if (
    instance &&
    instance.verticalPlacement.type === ComponentVerticalPlacementType.REPLICATION &&
    props.verticalPlacement?.type === ComponentVerticalPlacementType.REMAPPING
  ) {
    doc = docGuardComponentInstanceLevelElimination(doc, handle.id, "REPLICATION_TO_REMAPPING");
  }

  // Handle switching to a new component.
  if (instance && props.componentId && props.componentId !== instance.componentId) {
    if (componentChangeCreatesCircularDependency(doc, handle, props.componentId)) {
      throw new ComponentCircularDependencyErrorNotification(props.componentId);
    }

    // The level remapping still references the old component's levels.
    // Update the remapping to the new component's level.
    if (instance.verticalPlacement.type === ComponentVerticalPlacementType.REMAPPING) {
      const componentSourceLevels = docComponentSourceLevels(doc, props.componentId);
      if (componentSourceLevels.size > 1) {
        // Multi-level components cannot use remapping. Switch to replication.
        props = { ...props, verticalPlacement: createReplicationVerticalPlacement() };
      } else {
        // Switch the level remapping to the new component's source level.
        const targetLevelId = Object.values(instance.verticalPlacement.levelRemapping ?? {})[0];
        const [sourceLevelId] = componentSourceLevels;
        const levelRemapping = sourceLevelId && targetLevelId ? { [sourceLevelId]: targetLevelId } : {};
        props = { ...props, verticalPlacement: createRemappingVerticalPlacement(levelRemapping) };
      }
    }
  }

  return docUpdateEntity(doc, handle.type, handle.id, props as any);
}

export function docMergeEntityProps<T extends DocEntityHandle = DocEntityHandle>(
  doc: Doc,
  handle: T,
  props: RecursivePartial<DocEntityTypeToValueType[T["type"]]>,
): Doc {
  const value = props["labels"];
  if (value && docEntityHandleIsLabelable(handle)) {
    if (isComponentMemberEntityHandle(handle)) {
      for (const labelId in value) {
        if (docEntityHasReportableUnitLabelConflict(doc, handle, labelId)) {
          throw new ReportableUnitDesignatorLabelErrorNotification();
        }
      }
    }
    if (docLabelsBreakOptionSetReportableUnitCap(doc, handle, value as Table<boolean>)) {
      throw new MultipleReportableUnitLabelsErrorNotification();
    }
  }

  /**
   * The props argument is correctly typed for the function signature, but cast as any
   * below because the compiler can't infer that the props are valid for the given
   * handle type. We currently do not know why this is.
   */
  switch (handle.type) {
    case "DOC_REF":
    case "VERTEX":
    case "EDGE":
    case "EDIT_MODE":
    case "GENERATED_ENTITY":
    case "AUTO_DIMENSION":
    case "SYSTEM_PARAMETER":
    case "INDETERMINATE_ENTITY_TYPE":
    case "FLOOR_BAY":
    case "FLOOR_PERIMETER_OPENING":
    case "FLOOR_SPLIT_LINE":
      return doc;
    case "PROP":
      return docMergePropAttributesProps(doc, handle, props as any);
    case "FACADE_SPAN_STRATUM":
      return docMergeFacadeSpanStrataProps(doc, handle, props as any);
    case "FACADE_SWEEP_STRATUM":
      return docMergeFacadeSweepStrataProps(doc, handle, props as any);
    case "ELEVATION":
      return docMergeElevationOptionSet(docUpdateEntity(doc, handle.type, handle.id, props as any));
    case "SHAPE": {
      const path = docGetMeasurementShape(doc, handle);
      if (path) {
        return docMergeMeasurementShapeProps(doc, handle, props as any);
      }
      return doc;
    }

    case "ANNOTATION":
      return docMergeAnnotationProps(doc, handle, props as any);
    case "SITE_SHAPE":
      return docMergeSiteShapeProps(doc, handle, props as any);
    case "SETTINGS":
      return docMergeSettingsProps(doc, handle, props as any);
    case "EDGE_MODIFIER":
      return docMergeEdgeModifierProps(doc, handle, props as any);
    case "ROOF_SIDE":
      return docMergeRoofSideProps(doc, handle, props as any);
    case "CATALOG_ITEM":
      return docMergeCatalogItemProps(doc, handle, props as any);
    case "OPTION":
      return docMergeOptionProps(doc, handle, props as any);
    case "GLOBAL_PARAMETER":
    case "LOCAL_PARAMETER": {
      return docMergeParameterProps(doc, handle, props as any);
    }
    case "ELECTRICAL_RULE":
      return docMergeElectricalRuleProps(doc, handle, props as any);
    case "LABEL": {
      if (!labelNamePredicate(doc.labels, props as any)) {
        return doc;
      }
      return docUpdateEntity(doc, handle.type, handle.id, props as any);
    }
    case "DATA_TABLE": {
      const dataTable = docGetTableEntity(doc, handle) as DataTable;

      if (!dataTable) {
        console.error(`docMergeEntityProps: DATA_TABLE entity not found for handle ${handle.id}`);
        return doc;
      }

      const dataTableProps = props as Partial<DataTable>;
      const sortColumnIdOrder: DataTable["sortColumnIdOrder"] =
        dataTableProps.sortColumnIdOrder ?? dataTable.sortColumnIdOrder;

      // If there's a sortColumnId but no sortColumnIdOrder specified, set the sortColumnIdOrder to ASCENDING by default
      if (dataTableProps.sortColumnId && !sortColumnIdOrder) {
        Object.assign(dataTableProps, { sortColumnIdOrder: "ASCENDING" });
      }

      return docUpdateEntity(doc, handle.type, handle.id, dataTableProps);
    }
    case "FLOOR":
      return docMergeFloorProps(doc, handle, props as any);
    case "PLAN_VIEW": {
      const previousView = doc.planViews[handle.id];
      const updated = docUpdateEntity(doc, handle.type, handle.id, props as any);
      // Only a level membership change can move the view to another level or isolation world.
      return "levelIds" in props ? docSyncPlanViewHostLevel(updated, handle, previousView) : updated;
    }
    case "COMPONENT_INSTANCE":
      return docMergeComponentInstanceProps(
        doc,
        { type: "COMPONENT_INSTANCE", id: handle.id },
        props as RecursivePartial<ComponentInstance>,
      );
    case "FLOOR_SYSTEM":
    case "LEVEL":
    case "COMPOSITE_LEVEL":
    case "ROOF":
    case "ROOF_SYSTEM":
    case "ROOF_VOLUME":
    case "STAIR_BLOCK":
    case "SKYLIGHT":
    case "DORMER":
    case "STAIR_SYSTEM":
    case "ROOM_LAYOUT":
    case "ROOM_LAYOUT_INSTANCE":
    case "PROP_INSTANCE":
    case "ROOM":
    case "BLOCK":
    case "COLUMN":
    case "PALETTE":
    case "CASEWORK":
    case "CEILING":
    case "VIEW_3D":
    case "ELEVATION_VIEW":
    case "SITE":
    case "RAILING":
    case "RAILING_POST":
    case "FREEFORM_WALL":
    case "FOUNDATION_SYSTEM":
    case "FOUNDATION":
    case "UNDERLAY":
    case "ASSET":
    case "REFERENCE_GEOM":
    case "FACADE":
    case "SETTINGS_SET":
    case "ENTITY_SETTINGS":
    case "OPTION_TEMPLATE":
    case "OPENING":
    case "FLOOR_FINISH":
    case "DRAWING_REF":
    case "GROUP":
    case "OPTION_EFFECT":
    case "OPTION_RULE":
    case "OPTION_SET_OVERRIDES":
    case "OPTION_SET":
    case "INSTANCE_OPTION_SET":
    case "OPTION_LOCK":
    case "SHEET":
    case "ROOF_OPENING":
    case "MODIFIER":
    case "SELECTOR":
    case "VIEW_SELECTOR":
    case "VIEW_OVERRIDE":
    case "EDGE_BEHAVIOR":
    case "WALL_PLANE_ASSEMBLY":
    case "WALL_PLANE_FILTER":
    case "WALL_PLANE_RULE":
    case "WALL_PLANE_TRIM":
    case "ELECTRICAL_DEVICE":
    case "ELECTRICAL_LAYOUT":
    case "MEASUREMENT":
    case "PLAN_SET_TEMPLATE":
    case "CONTENT_TEMPLATE":
    case "CONTENT_TEMPLATE_INSTANCE":
    case "CONTENT_REPLICATOR":
    case "VIEWPORT":
    case "VIEW_TEMPLATE":
    case "DATA_SEQUENCE":
    case "PRESET":
    case "PROFILE":
    case "RAILING_SETTING":
    case "SECTION_VIEW":
    case "PLAN_SET_TEMPLATE_CONFIG":
    case "COMPONENT":
    case "VIEW_TEMPLATE_OVERRIDE":
    case "EFFECT":
    case "DATUM":
    case "RULE_BASED_SELECTOR":
    case "RULE_BASED_SELECTOR_INSTANCE":
    case "FRAMING_COMPONENT":
    case "FOUNDATION_ASSEMBLY":
    case "FOUNDATION_COMPONENT":
    case "HEADER_ASSEMBLY":
    case "FRAME_FLOOR_ASSEMBLY":
    case "SLAB_FLOOR_ASSEMBLY":
    case "SLAB_COMPONENT":
    case "SHEATHING_COMPONENT":
    case "MASONRY_COMPONENT":
    case "CIP_COMPONENT":
    case "REPORTABLE_UNIT_DESIGNATION":
    case "STAIR_SETTING":
      return docUpdateEntity(doc, handle.type, handle.id, props as any);
    default:
      assertUnreachable(handle);
  }
}
